import {defineStore, storeToRefs} from "pinia";
import axios from "axios";
import {ErrorDescription} from "./ErrorDescription";
import {
    BFieldSelection,
    BFilter,
    BFilterOptions,
    BFilterableField,
    DicraLastNameOperations,
    dateTimeOperations,
    listOperations,
    textOperations,
} from "@/components/BFilterEditor/BFilterEditor.types";
import {BSortOptions, BTableColumn, BTableOptions, serializeSortBy} from "@/components/BTable/BTable.types";
import {
    FailedFileDetails,
    FetchValidationMessagesResponse,
    Ori,
    SelectItem,
    UploadType,
    UploadedFileDetails,
} from "@/types";
import {
    defaultStatisticsColumns,
    defaultValidationMessagesColumns,
    defaultValidationMessagesFilterableFields,
} from "@/tableDefaults";
import {ref, computed} from "vue";
import {useBaseStore} from "./baseStore";
import {useOriStore} from "./oriStore";
import {serializeFilters} from "@/components/BTable/HelperFunctions";
import {Death, DeathForm, DeathZeroReport, Submission} from "@/components/DcraEditor/schema";
import { CodeLabelMap, CodeLabelMapByKind, IncommingDataForSelects } from "./CodeLabelMapByKind";
import { handleErrorInResponse } from "@/utils";

export const useDcraStore = defineStore("dcraStore", () => {
    var baseStore = useBaseStore();
    const apiUrl = "api/v1/dcra";
    baseStore.apiUrl.value = apiUrl;
    baseStore.dataset.value = UploadType.dcra;
    const loading = ref<boolean>(false);

    var oriStore = useOriStore();
    oriStore.dataset = undefined; 

    var getColumns = computed(() => {
        return [
            {
                key: "decedentSurname",
                text: "Decedent Surname",
                sortable: false,
                value: (item) => item["decedentSurname"],
            },
            {
                key: "lineNumber",
                text: "Line Number",
                sortable: true,
                columnClass: ["d-none"],
                class: (item) => ["d-none"],
                value: (item) => item["lineNumber"],
            },
            {
                key: "errorCode",
                text: "Error Code",
                sortable: true,
                value: (item) => item["errorCode"],
            },
            {
                key: "errorType",
                text: "Type",
                sortable: true,
                value: (item) => item["errorType"],
            },
            {
                key: "description",
                text: "Description",
                sortable: true,
                value: (item) => item.description,
            },
        ] as BTableColumn[];
    });

    var getStatisticsColumns = computed(() => {
        return defaultStatisticsColumns;
    });

    var getFilterableFields = computed(() => {
        return [
            {
                property: "decedent",
                label: "Decedent Surname",
                operations: textOperations,
                editorComponent: "text-editor",
            },
            {
                property: "errorCode",
                label: "Error Code",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.errorCodes,
            },
            {
                property: "errorType",
                label: "Type",
                operations: listOperations,
                editorComponent: "list-editor",
                editorOptions: baseStore.getTypes,
            },
        ] as BFilterableField[];
    });

    var getValidationMessagesColumns = computed(() => {
        return defaultValidationMessagesColumns;
    });

    var getValidationMessagesFilterableFields = computed(() => {
        return defaultValidationMessagesFilterableFields;
    });

    var showWarningDetails = computed(() => {
        return true;
    });

    var errorCodes = computed(() => {
        return baseStore.errorCodes;
    });

    var errorDescriptions = computed(() => {
        return baseStore.errorDescriptions;
    });

    function getExportUrl(dateRange: Date[], filterInfo: BFilterOptions, filterableFields: BFilterableField[]) {
        let filterText = serializeFilters(filterInfo.filters, filterableFields);
        let dateString = "";

        dateRange.forEach((f: Date, i: Number) => {
            dateString += `,${f.getFullYear()}-${f.getMonth() + 1}-${f.getDate()}`;
        });

        return `/export-data?dateRange=${encodeURIComponent(dateString.substring(1))}&filter=${encodeURIComponent(
            filterText ?? ""
        )}`;
    }

    function getValidationMessagesForFile(
        id: number,
        pageNumber: number,
        pageSize: number,
        filterInfo: BFilter[],
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getValidationMessagesForFile(
            id,
            pageNumber,
            pageSize,
            filterInfo,
            filterableFields,
            sortingInfo
        );
    }

    function getRecordDetails(id: number) {
        return baseStore.getRecordDetails(id);
    }

    function getErrorsAndWarnings(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getErrorsAndWarningsBase(`${apiUrl}/errors-warnings`, tableOptions, filters, filterableFields);
    }

    function getErrorsAndWarningsForFile(
        id: number,
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        return baseStore.getErrorsAndWarningsForUploadDetails(id, tableOptions, filters, filterableFields); 
    }

    function getErrorsAndWarningsColumns(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        let ewColumns = baseStore.getErrorsAndWarningsColumns(
            onlyHasOneOri,
            showAdditionalColumns,
            onlyIncludePrimaryColumns
        );

        var decedentColumn = ewColumns.find((f) => f.key == "incidentNumber");
        if (decedentColumn != undefined && ewColumns.includes(decedentColumn)) {
            decedentColumn.key = "decedentSurname";
            decedentColumn.text = "Decedent Surname";
            decedentColumn.sortable = false;
            decedentColumn.value = (item) => item.decedentSurname;
            decedentColumn.filterable = {
                label: "Decedent Surname",
                property: "decedentSurname",
                operations: DicraLastNameOperations,
                editorComponent: "text-editor",
            };
            decedentColumn.columnType = "primary";
        }

        return ewColumns;
    }

    function getFilterableFieldsForErrorsAndWarnings(
        onlyHasOneOri: boolean,
        showAdditionalColumns: boolean,
        onlyIncludePrimaryColumns: boolean
    ) {
        var columns = getErrorsAndWarningsColumns(onlyHasOneOri, showAdditionalColumns, onlyIncludePrimaryColumns);
        var ewFilters = [] as BFilterableField[];

        columns
            .filter((col: BTableColumn) => col.filterable != undefined)
            .map((col: BTableColumn) => ewFilters.push(col.filterable as BFilterableField));

        var agencyFilter = ewFilters.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return ewFilters;
    }

    function getFiles(
        filters: BFilter[],
        tableOptions: BTableOptions,
        filterableFields: BFilterableField[],
        sortingInfo: BSortOptions[]
    ) {
        // if there's no total then were going to return the requested row count plus 1 so that we know when to disable to next page button
        return baseStore.getFiles(tableOptions, filters ?? [], filterableFields);
    }
    var getFileUploadColumns = baseStore.getFileUploadColumns;
    function getFilterableFieldsForFileUploads(onlyHasOneOri: boolean, submissionPeriodHidden: boolean) {
        var filterableFields = baseStore.getFilterableFieldsForFileUploads(onlyHasOneOri, submissionPeriodHidden);

        var agencyFilter = filterableFields.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return filterableFields;
    }
    var errorDescriptionsDownloadUrl = ref(baseStore.errorDescriptionsDownloadUrl);
    //DCRA Editor * start *
    var AddEditColumns = computed(() => {
        return [
            {
                key: "ori",
                text: "Ori",
                sortable: true,
                value: (item) => item.ori,
                filterable: undefined,
                columnType: "secondary",
            },
            {
                key: "agency",
                text: "Agency",
                sortable: true,
                value: (item) => item.agency,
                filterable: {
                    label: "Agency",
                    property: "agency",
                    operations: listOperations,
                    editorComponent: "list-editor",
                },
                columnType: "primary",
            },

            {
                key: "decedentSurname",
                text: "Decedent Surname",
                sortable: false,
                value: (item) => item["decedentSurname"],
                filterable: {
                    label: "Decedent Surname",
                    property: "decedentSurname",
                    operations: DicraLastNameOperations,
                    editorComponent: "text-editor",
                },
                columnType: "primary",
            },
            {
                key: "deathDate",
                text: "Date of Death",
                sortable: true,
                class: (item) => ["text-nowrap"],
                value: (item) => item["deathDate"],
                filterable: {
                    label: "Date of Death",
                    property: "deathDate",
                    operations: dateTimeOperations,
                    editorComponent: "date-editor",
                },
                columnType: "primary",
            },
            {
                key: "status",
                text: "Status",
                sortable: true,
                class: (item) => ["text-nowrap"],
                value: (item) => item["status"],
                filterable: {
                    label: "Status",
                    property: "status",
                    operations: listOperations,
                    editorComponent: "list-editor",
                    editorOptions: getStatusTypes,
                },
                columnType: "primary",
            },
            {
                key: "submittedBy",
                text: "Submitted By",
                sortable: true,
                class: (item) => ["text-nowrap"],
                value: (item) => item["submittedBy"],
                filterable: {
                    label: "Submitted By",
                    property: "submittedBy",
                    operations: textOperations,
                    editorComponent: "text-editor",
                },
                columnType: "primary",
            },
            {
                key: "actions",
                text: "",
                sortable: false,
                columnClass: ["align-top"],
                class: (item) => ["text-nowrap text-end"],
                value: (item) => item["link"],
            }
        ] as BTableColumn[];
    });    

    const addEditFilterableFields = computed(() => {
        let filters = [] as BFilterableField[];

        AddEditColumns.value
            .filter((col: BTableColumn) => col.filterable != undefined)
            .map((col: BTableColumn) => filters.push(col.filterable as BFilterableField));

        var agencyFilter = filters.find((f) => f.property == "agency");
        if (agencyFilter) {
            agencyFilter.editorOptions = oriStore.getOrisAsBFieldSelectionArray;
        }

        return filters;
    });

    const addEditFilterOptions = ref({
        filters: [],
    } as BFilterOptions);

    async function fetchListOfDeaths(dcraEditTableOptions:BTableOptions) {
        let filterText = addEditFilterOptions.value?.filters
            ? serializeFilters(addEditFilterOptions.value.filters, addEditFilterableFields.value)
            : "";
        let sortOptionsText = serializeSortBy(dcraEditTableOptions.sortBy);

        return axios.get(`${apiUrl}/deaths`, {
            params: {
                filter: filterText,
                sort: sortOptionsText,
                page: dcraEditTableOptions.page,
                pageSize: dcraEditTableOptions.itemsPerPage,
                pagingMode: dcraEditTableOptions.pagingMode,
            },
        });       
    }

    async function deleteDeath(id:number){
        var response = await axios.delete(`${apiUrl}/deaths/${id}`);

        listOfDeaths.value = listOfDeaths.value.filter(obj => obj.id !== id);

        return response;
    }

    async function fetchDeath(id:number){
        return await axios.get<DeathForm>(`${apiUrl}/deaths/${id}`)
    }

    var listOfDeaths = ref([] as Death[]);
    
    var codeLabelByKind = ref([] as CodeLabelMapByKind[]);

    async function fetchCodeLabelByKind() {
        try {
            const data = await axios.get<IncommingDataForSelects[]>(`${apiUrl}/code-label-map`);
            if (data.data) {
                data.data.forEach((f) => {
                    if (codeLabelByKind.value.filter((x) => x.kind == f.kind).length === 0) {
                        let codeLabelMap = data.data
                            .filter((x) => x.kind == f.kind)
                            ?.map((m) => ({ code: m.code, label: m.label } as CodeLabelMap));
                        codeLabelByKind.value.push({ kind: f.kind, codes: codeLabelMap });
                    }
                });
            }
        } catch (error) {
            handleErrorInResponse(error);
        }
    }
    
    var getCodeLabels = computed(() => {
        return (kind: string) =>
            codeLabelByKind.value.find((f) => f.kind == kind)?.codes;
    });

    var getSex = computed(() => {
        let filterList = getCodeLabels.value;
        const genders = filterList("Gender") as CodeLabelMap[];

        return genders?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getEthnicity = computed(() => {
        let filterList = getCodeLabels.value;
        const ethnicity = filterList("Ethnicity") as CodeLabelMap[];

        return ethnicity?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });

    var getRace = computed(() => {
        let filterList = getCodeLabels.value;
        const race = filterList("Race") as CodeLabelMap[];

        return race?.map((m) => ({ label: m.label, value: m.code } as SelectItem));
    });

    var getEventLocationType = computed(() => {
        let filterList = getCodeLabels.value;
        const locationType = filterList("LocationType") as CodeLabelMap[];

        return locationType?.map((m) => ({ label: m.label, value: m.code } as SelectItem));
    });

    var getMannerOfDeath = computed(() => {
        let filterList = getCodeLabels.value;
        const mannerOfDeath = filterList("CauseOfDeath") as CodeLabelMap[];

        return mannerOfDeath?.map((m) => ({ label: m.label, value: m.code } as SelectItem));
    });
    var getState = computed(() => {
        let filterList = getCodeLabels.value;
        const states = filterList("State") as CodeLabelMap[];

        return states?.map(
            (m) => ({ label: m.label, value: m.code } as SelectItem)
        );
    });
    var getStatusTypes = computed(() => {
        let statusTypes = [{value:'2',text:'Accepted'},{value:'1',text:'Has Error(s)'}] as BFieldSelection[];

        return statusTypes;
    });
    function editDeath(id:number, form:DeathForm) {
        return axios.put(`${apiUrl}/deaths/${id}`,form);
    }

    function createDeath(form:DeathForm) {
        return axios.post(`${apiUrl}/deaths/`,form);
    }

    function getDeathErrors(id:number){
        return axios.get<string[]>(`${apiUrl}/deaths/${id}/errors`);
    }

    function addZeroReport(form:DeathZeroReport) {
        return axios.post(`${apiUrl}/zero-report/`,form);
    }
    //DCRA Editor * end *

    //DCRA Submissions
    function getSubmissionItems(tableOptions:BTableOptions){       

        return axios.get<Submission[]>(`${apiUrl}/death-submission`, {
            params: {                
                page: tableOptions.page,
                pageSize: tableOptions.itemsPerPage
            },
        });     
    }

    //ori related functions & computed properties
    const {
        oris,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        getSelectedOri                
    } = storeToRefs(oriStore);
    var fetchDatasetDetailsForOri = oriStore.fetchDatasetDetailsForOri;
    var reset = oriStore.reset;
    var setSelectedOri = oriStore.setSelectedOri;
    var checkIfDownloadValid = baseStore.checkIfDownloadValid;
    var getOriListForDataset = oriStore.getOriListForDataset;
    var loadErrorsSummary = baseStore.loadErrorsSummary;
    var datasetSummary = baseStore.datasetSummary;
    var loadStatisticsSummary = baseStore.loadStatisticsSummary;
    var datasetStatisticsSummary = baseStore.datasetStatisticsSummary;
    var loadLastSubmission = baseStore.loadLastSubmission;
    var datasetLastSubmissions = baseStore.datasetLastSubmissions;

    return {
        getColumns,
        getStatisticsColumns,
        getFilterableFields,
        getValidationMessagesColumns,
        getValidationMessagesFilterableFields,
        showWarningDetails,
        errorCodes,
        errorDescriptions,
        getExportUrl,
        getValidationMessagesForFile,
        getRecordDetails,
        getErrorsAndWarnings,
        getErrorsAndWarningsForFile,
        getFiles,
        getOriName,
        getOrisForSelectList,
        getOrisAsBFieldSelectionArray,
        getDetailsForCurrentDataset,
        fetchDatasetDetailsForOri,
        reset,
        getSelectedOri,
        setSelectedOri,
        errorDescriptionsDownloadUrl,
        getFileUploadColumns,
        getFilterableFieldsForFileUploads,
        getErrorsAndWarningsColumns,
        getFilterableFieldsForErrorsAndWarnings,
        oris,
        checkIfDownloadValid,
        loadErrorsSummary,
        datasetSummary,
        loadStatisticsSummary,
        datasetStatisticsSummary,
        loadLastSubmission,
        datasetLastSubmissions,
        apiUrl,
        AddEditColumns,
        loading,
        addEditFilterOptions,
        addEditFilterableFields,
        fetchListOfDeaths,
        listOfDeaths,
        deleteDeath,
        fetchDeath,
        fetchCodeLabelByKind,
        getSex,
        getEthnicity,
        getRace,
        getEventLocationType,
        getMannerOfDeath,
        getState,
        createDeath,
        editDeath,
        getStatusTypes,
        getDeathErrors,
        getSubmissionItems,
        addZeroReport,
        getOriListForDataset
    };
});
